import Vue from 'vue';
import axios from 'axios';
import get from 'lodash/get';

export const ADD_SELECTED_PRODUCT = 'selectedProducts/ADD_SELECTED_PRODUCT';
export const UPDATE_SELECTED_PRODUCT = 'selectedProducts/UPDATE_SELECTED_PRODUCT';
export const REMOVE_SELECTED_PRODUCT = 'selectedProducts/REMOVE_SELECTED_PRODUCT';
export const DELETE_SELECTED_PRODUCTS = 'selectedProducts/DELETE_SELECTED_PRODUCTS';
export const CLEAR_SELECTED_PRODUCTS = 'selectedProducts/CLEAR_SELECTED_PRODUCTS';

const cartModule = {
  namespaced: true,

  state: {
    bikes: [],
    parts: [],
    customWaterBottles: [],
    projectOneBikes: [],
    cartSummary: {
      bikes: {},
      parts: {},
      customWaterBottles: {},
      projectOneBikes: {},
      total: '',
      hasAllocatedItems: false,
    },
    cartId: '',
    undoEntries: null,
    fetchCallback: null,
    selectedProducts: [],
    hasAllocatedItemsProcessing: false,
    hasCartValidationError: false,
  },

  getters: {
    selectedProducts: (state) => state.selectedProducts,
    selectedProductsBikeCount: (state) =>
      state.selectedProducts.filter((product) => product.productType === 'bike').length,
    selectedProductsAccessoriesCount: (state) =>
      state.selectedProducts.filter((product) => product.productType === 'am').length,
  },

  mutations: {
    setBikes(state, payload) {
      Vue.set(state, 'bikes', payload);
    },

    setParts(state, payload) {
      Vue.set(state, 'parts', payload);
    },

    setCustomWaterBottles(state, payload) {
      Vue.set(state, 'customWaterBottles', payload);
    },

    setProjectOneBikes(state, payload) {
      Vue.set(state, 'projectOneBikes', payload);
    },

    setCartSummary(state, payload) {
      Vue.set(state, 'cartSummary', payload);
    },

    setCartId(state, payload) {
      Vue.set(state, 'cartId', payload);
    },

    setUndoEntries(state, payload) {
      Vue.set(state, 'undoEntries', payload);
    },

    setFetchCallback(state, payload) {
      Vue.set(state, 'fetchCallback', payload);
    },

    setHasAllocatedItemsProcessing(state, payload) {
      state.hasAllocatedItemsProcessing = payload;
    },

    setHasValidationError(state, payload) {
      state.hasCartValidationError = payload;
    },

    [ADD_SELECTED_PRODUCT](state, payload) {
      if (!state.selectedProducts.some((product) => product.cartEntryPk === payload.cartEntryPk)) {
        state.selectedProducts.push(payload);
      }
    },
    [UPDATE_SELECTED_PRODUCT](state, payload) {
      if (payload.lineNote) {
        Vue.set(
          state.selectedProducts[
            state.selectedProducts.findIndex((product) => product.cartEntryPk === payload.cartEntryPk)
          ],
          'lineNote',
          payload.lineNote
        );
      }
      if (payload.qty) {
        Vue.set(
          state.selectedProducts[
            state.selectedProducts.findIndex((product) => product.cartEntryPk === payload.cartEntryPk)
          ],
          'qty',
          Number(payload.qty)
        );
      }
    },
    [REMOVE_SELECTED_PRODUCT](state, payload) {
      state.selectedProducts = payload;
    },
    [DELETE_SELECTED_PRODUCTS](state, payload) {
      state.selectedProducts = payload;
    },
    [CLEAR_SELECTED_PRODUCTS](state, payload) {
      state.selectedProducts = payload;
    },
  },

  actions: {
    addSelectedProduct({commit}, payload) {
      commit(ADD_SELECTED_PRODUCT, payload);
    },
    updateSelectedProduct({commit}, payload) {
      commit(UPDATE_SELECTED_PRODUCT, payload);
    },
    removeSelectedProduct({state, commit}, payload) {
      commit(
        REMOVE_SELECTED_PRODUCT,
        state.selectedProducts.filter((product) => product.cartEntryPk !== payload)
      );
    },
    deleteSelectedProducts({state, dispatch, commit}, payload) {
      if (payload === 'bikes' || payload === 'projectOneBikes') {
        dispatch(
          'updateCart',
          state.selectedProducts
            .filter((product) => product.productType === 'bike')
            .map((product) => {
              return {
                sku: product.sku,
                cartEntryPk: product.cartEntryPk,
                qty: 0,
              };
            })
        );
        commit(
          DELETE_SELECTED_PRODUCTS,
          state.selectedProducts.filter((product) => product.productType !== 'bike')
        );
      } else {
        dispatch(
          'updateCart',
          state.selectedProducts
            .filter((product) => product.productType !== 'bike')
            .map((product) => {
              return {
                sku: product.sku,
                qty: 0,
                cartEntryPk: product.cartEntryPk,
                productRecipeID: product.productRecipeID,
              };
            })
        );
        commit(
          DELETE_SELECTED_PRODUCTS,
          state.selectedProducts.filter((product) => product.productType === 'bike')
        );
      }
    },
    clearSelectedProducts({state, commit}, payload) {
      if (payload === 'bikes' || payload === 'projectOneBikes') {
        commit(
          CLEAR_SELECTED_PRODUCTS,
          state.selectedProducts.filter((product) => product.productType !== 'bike')
        );
      } else if (payload === 'parts') {
        commit(
          CLEAR_SELECTED_PRODUCTS,
          state.selectedProducts.filter((product) => product.productType !== 'am' || product.productRecipeID)
        );
      } else if (payload === 'customWaterBottles') {
        commit(
          CLEAR_SELECTED_PRODUCTS,
          state.selectedProducts.filter((product) => product.productType !== 'am' || !product.productRecipeID)
        );
      }
    },
    fetchCart(context) {
      return axios.get('/cart/cartInfo?includes=bikes,parts,customWaterBottles,projectOne').then(({data}) => {
        if (data && data.data) {
          if (data.data.cartSummary) {
            context.commit('setBikes', data.data.bikes);
            context.commit('setParts', data.data.parts);
            context.commit('setCustomWaterBottles', data.data.customWaterBottles);
            context.commit('setProjectOneBikes', data.data.projectOneBikes);
            context.commit('setCartSummary', data.data.cartSummary);
            context.commit('setCartId', data.data.cartId);
          } else {
            context.commit('setBikes', []);
            context.commit('setParts', []);
            context.commit('setCustomWaterBottles', []);
            context.commit('setProjectOneBikes', []);
            context.commit('setCartSummary', {});
            context.commit('setCartId', '');
          }
        }

        if (context.state.fetchCallback) {
          context.state.fetchCallback();
        }
      });
    },

    updateCart({state, dispatch}, products, deleteSingleProductFlag = false) {
      return axios
        .put(`/cart/carts/${state.cartId}/entries`, {cartEntries: products})
        .then((response) => {
          // Display error toast if attempting to add more products than the distributor containers can hold
          const feedback = get(response, 'data.meta.feedback');
          if (get(feedback, 'message.basePropertyKey') === 'distributor.B2B.skuGrid.containerFull') {
            window.vm.$notify({
              type: feedback.type,
              message: get(feedback, 'message.basePropertyValue'),
              showClose: true,
              duration: get(feedback, 'type.toastDuration'),
            });
          }
          return response;
        })
        .finally(() => {
          if (!deleteSingleProductFlag) {
            dispatch('fetchCart');
          }
        });
    },

    removeProducts({state, commit, dispatch}, partTypeKey) {
      let productsToRemoveFromCart = [];
      if (partTypeKey === 'bikes') {
        const bikeList = state.selectedProducts.filter((product) => product.productType === 'bike');
        commit('setUndoEntries', bikeList);
        productsToRemoveFromCart = bikeList.map((product) => {
          return {
            sku: product.sku,
            cartEntryPk: product.cartEntryPk,
            qty: 0,
          };
        });
      } else {
        const accessoriesList = state.selectedProducts.filter((product) => product.productType !== 'bike');
        commit('setUndoEntries', accessoriesList);
        productsToRemoveFromCart = accessoriesList.map((product) => {
          return {
            sku: product.sku,
            cartEntryPk: product.cartEntryPk,
            qty: 0,
          };
        });
      }

      dispatch('clearSelectedProducts', partTypeKey);
      if (productsToRemoveFromCart.length) {
        axios.put(`/cart/carts/${state.cartId}/entries`, {cartEntries: productsToRemoveFromCart}).finally(() => {
          dispatch('fetchCart');
        });
      }
    },

    undoRemove(context) {
      if (context.state.undoEntries) {
        axios.put(`/cart/entries`, {items: context.state.undoEntries}).finally(() => {
          context.dispatch('fetchCart');
        });
        context.commit('setUndoEntries', null);
      }
    },
  },
};

export default cartModule;
